import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, Loader2 } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import FloatingCart from "@/components/layout/FloatingCart";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import SEOHead from "@/components/SEOHead";

const Contact = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", subject: "", message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const { error } = await supabase.from("contact_submissions").insert({
        name: formData.name, email: formData.email,
        phone: formData.phone || null, subject: formData.subject || null,
        message: formData.message,
      });
      if (error) throw error;
      toast.success(t.contact.successMsg);
      setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (error: any) {
      toast.error("Failed to send message: " + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: MapPin, title: t.contact.visitUs, lines: t.contact.address },
    { icon: Phone, title: t.contact.callUs, lines: ["+201004836927"] },
    { icon: Mail, title: t.contact.emailUs, lines: ["bayt.altib@gmail.com"] },
    { icon: Clock, title: t.contact.openingHours, lines: t.contact.hours },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Contact Us"
        description="Get in touch with Bait Al-Teeb. Reach out for questions about our luxury fragrances or place a custom order."
        canonical="https://bait-al-teeb-essence.lovable.app/contact"
      />
      <Navbar />
      <FloatingCart />
      <main className="pt-32 pb-20">
        <div className="container-luxury">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-16">
            <span className="text-primary text-sm tracking-[0.3em] uppercase">{t.contact.badge}</span>
            <h1 className="text-5xl md:text-6xl font-serif mt-4 mb-6">{t.contact.title}</h1>
            <div className="divider-gold w-32 mx-auto" />
            <p className="text-muted-foreground mt-6 max-w-xl mx-auto">{t.contact.description}</p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
              <h2 className="text-2xl font-serif mb-8">{t.contact.sendMessage}</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm tracking-wider uppercase text-muted-foreground mb-2 block">{t.contact.yourName}</label>
                    <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full bg-card border border-border rounded-sm px-4 py-3 text-foreground focus:outline-none focus:border-primary transition-colors" required />
                  </div>
                  <div>
                    <label className="text-sm tracking-wider uppercase text-muted-foreground mb-2 block">{t.contact.phoneNumber}</label>
                    <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="w-full bg-card border border-border rounded-sm px-4 py-3 text-foreground focus:outline-none focus:border-primary transition-colors" required />
                  </div>
                </div>
                <div>
                  <label className="text-sm tracking-wider uppercase text-muted-foreground mb-2 block">{t.contact.emailAddress}</label>
                  <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full bg-card border border-border rounded-sm px-4 py-3 text-foreground focus:outline-none focus:border-primary transition-colors" required />
                </div>
                <div>
                  <label className="text-sm tracking-wider uppercase text-muted-foreground mb-2 block">{t.contact.subject}</label>
                  <input type="text" value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} className="w-full bg-card border border-border rounded-sm px-4 py-3 text-foreground focus:outline-none focus:border-primary transition-colors" required />
                </div>
                <div>
                  <label className="text-sm tracking-wider uppercase text-muted-foreground mb-2 block">{t.contact.message}</label>
                  <textarea value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} rows={6} className="w-full bg-card border border-border rounded-sm px-4 py-3 text-foreground focus:outline-none focus:border-primary transition-colors resize-none" required />
                </div>
                <Button variant="bronze" size="lg" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t.contact.sending}</>) : t.contact.sendBtn}
                </Button>
              </form>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}>
              <h2 className="text-2xl font-serif mb-8">{t.contact.contactInfo}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {contactInfo.map((info, index) => (
                  <motion.div key={info.title} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 + index * 0.1 }} className="p-6 bg-card border border-border rounded-sm">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                      <info.icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="font-serif text-lg mb-2">{info.title}</h3>
                    {info.lines.map((line: string, i: number) => {
                      if (/^\+?\d[\d\s\-().]+$/.test(line)) {
                        return <a key={i} href={`tel:${line.replace(/\s/g, "")}`} className="text-muted-foreground text-sm hover:text-primary transition-colors block">{line}</a>;
                      }
                      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(line)) {
                        return <a key={i} href={`mailto:${line}`} className="text-muted-foreground text-sm hover:text-primary transition-colors block">{line}</a>;
                      }
                      return <p key={i} className="text-muted-foreground text-sm">{line}</p>;
                    })}
                  </motion.div>
                ))}
              </div>
              <div className="mt-8 aspect-video bg-card border border-border rounded-sm flex items-center justify-center">
                <p className="text-muted-foreground">{t.contact.mapPlaceholder}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Contact;
